import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface CreateServiceRequestFromProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  problem: any;
  onSuccess?: (serviceRequestNumber: string) => void; // Optional callback when service request is created
}

const CreateServiceRequestFromProblemModal: React.FC<CreateServiceRequestFromProblemModalProps> = ({
  isOpen,
  onClose,
  problem,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Search queries for comboboxes
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [impactQuery, setImpactQuery] = useState('');
  const [urgencyQuery, setUrgencyQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');

  const [formData, setFormData] = useState({
    serviceRequestState: 'new',
    impact: '',
    urgency: '',
    priority: '',
    shortDescription: '',
    customerId: '',
    engagementId: '',
    contactId: '',
    servicePlanId: '',
    resourceId: '',
    resourceIdValue: '',
    otherResourceNames: '',
    assignmentGroupId: '',
    assignedTo: ''
  });

  const [isOtherResource, setIsOtherResource] = useState(false);
  // Store resource object separately for reliable display
  const [selectedResource, setSelectedResource] = useState<{ id: string; name: string; resourceId: string } | null>(null);

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements based on customer
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return { engagements: [] };
      const response = await api.get(`/engagements?customerId=${formData.customerId}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch contacts for Contact dropdown - filtered by customer and engagement
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.customerId, formData.engagementId, contactQuery],
    queryFn: async () => {
      if (!formData.customerId || !formData.engagementId) return { users: [] };
      const params = new URLSearchParams({
        class: 'contact',
        customerId: formData.customerId,
        engagementId: formData.engagementId,
        limit: '100'
      });
      if (contactQuery) {
        params.append('search', contactQuery);
      }
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId && !!formData.engagementId
  });

  // Fetch primary contact for auto-selection
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch service plans based on engagement
  const { data: servicePlansData } = useQuery({
    queryKey: ['service-plans', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return { plans: [] };
      const response = await api.get(`/engagements/${formData.engagementId}/service-plans`);
      return response.data;
    },
    enabled: !!formData.engagementId
  });

  // Fetch assignment groups (only groups with type 'assignment')
  const { data: groupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    }
  });

  // Fetch resources based on engagement
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return [];
      const response = await api.get(`/resources?engagementId=${formData.engagementId}`);
      return Array.isArray(response.data) ? response.data : (response.data.resources || []);
    },
    enabled: !!formData.engagementId
  });

  // Fetch users based on assignment group
  const { data: usersData } = useQuery({
    queryKey: ['group-users', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      return fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroupId
  });

  // Priority calculation based on Impact and Urgency
  const calculatePriority = (impact: string, urgency: string): string => {
    // Normalize values to handle both 'low' and 'low_3' formats
    const normalizeValue = (value: string): string => {
      if (!value) return 'low';
      if (value.startsWith('low')) return 'low';
      if (value.startsWith('medium')) return 'medium';
      if (value.startsWith('high')) return 'high';
      return value;
    };

    const normalizedImpact = normalizeValue(impact);
    const normalizedUrgency = normalizeValue(urgency);

    // Priority Matrix:
    // Impact\Urgency | High | Medium | Low
    // High           | 1    | 2      | 3
    // Medium         | 2    | 3      | 4
    // Low            | 3    | 4      | 5

    const matrix: Record<string, Record<string, string>> = {
      'high': { 'high': 'critical_1', 'medium': 'high_2', 'low': 'moderate_3' },
      'medium': { 'high': 'high_2', 'medium': 'moderate_3', 'low': 'low_4' },
      'low': { 'high': 'moderate_3', 'medium': 'low_4', 'low': 'planning_5' }
    };

    return matrix[normalizedImpact]?.[normalizedUrgency] || 'moderate_3';
  };

  // Auto-calculate priority when impact or urgency changes
  useEffect(() => {
    if (formData.impact && formData.urgency) {
      const calculatedPriority = calculatePriority(formData.impact, formData.urgency);
      setFormData(prev => ({ ...prev, priority: calculatedPriority }));
    }
  }, [formData.impact, formData.urgency]);

  // Find and set resource when resourceId, resourceIdValue, or resources change
  useEffect(() => {
    if (resourcesData && !isOtherResource && (formData.resourceId || formData.resourceIdValue)) {
      const resourcesArray = Array.isArray(resourcesData) ? resourcesData : [];

      // Try to find by resourceId (UUID) first
      let resource = formData.resourceId
        ? resourcesArray.find((r: any) => r.id === formData.resourceId)
        : null;

      // If not found by ID, try by resourceIdValue (subscription string)
      if (!resource && formData.resourceIdValue) {
        resource = resourcesArray.find((r: any) => r.resourceId === formData.resourceIdValue);
      }

      if (resource) {
        setSelectedResource({
          id: resource.id,
          name: resource.name || '',
          resourceId: resource.resourceId || ''
        });
        // Update formData if resourceId wasn't set or doesn't match
        if (!formData.resourceId || formData.resourceId !== resource.id) {
          setFormData(prev => ({
            ...prev,
            resourceId: resource.id,
            resourceIdValue: resource.resourceId || prev.resourceIdValue
          }));
        } else if (!formData.resourceIdValue && resource.resourceId) {
          setFormData(prev => ({ ...prev, resourceIdValue: resource.resourceId || '' }));
        }
      }
    } else if (!formData.resourceId && !isOtherResource) {
      // Clear resourceIdValue when resource is cleared
      setFormData(prev => ({ ...prev, resourceIdValue: '' }));
      setSelectedResource(null);
    }
  }, [formData.resourceId, formData.resourceIdValue, resourcesData, isOtherResource]);

  // Auto-select primary contact when customer/engagement changes
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId]);

  // Pre-fill form when problem data changes
  useEffect(() => {
    if (problem && isOpen) {
      // Check if problem has otherResourceNames
      const hasOtherResourceNames = problem.otherResourceNames && problem.otherResourceNames.trim() !== '';

      setFormData({
        serviceRequestState: 'new',
        impact: 'low_3',
        urgency: 'low_3',
        priority: 'planning_5', // Low + Low = Planning
        shortDescription: '',
        customerId: problem.customerId || '',
        engagementId: problem.engagementId || '',
        contactId: '',
        servicePlanId: '',
        resourceId: hasOtherResourceNames ? '' : (problem.resourceId || problem.Resource?.id || ''),
        resourceIdValue: hasOtherResourceNames ? '' : (problem.resourceIdValue || problem.Resource?.resourceId || ''),
        otherResourceNames: problem.otherResourceNames || '',
        assignmentGroupId: problem.assignmentGroupId || '',
        assignedTo: ''
      });
      setIsOtherResource(hasOtherResourceNames);

      // Set selected resource if Resource is available (backend returns Resource, not EngagementResource)
      if (!hasOtherResourceNames && problem.Resource) {
        setSelectedResource({
          id: problem.Resource.id,
          name: problem.Resource.name || '',
          resourceId: problem.Resource.resourceId || ''
        });
      } else {
        setSelectedResource(null);
      }

      setErrorMessage(null);
      setValidationErrors({});
      // Reset search queries
      setCustomerQuery('');
      setEngagementQuery('');
      setContactQuery('');
      setResourceQuery('');
      setAssignmentGroupQuery('');
      setAssignedToQuery('');
      setImpactQuery('');
      setUrgencyQuery('');
      setServicePlanQuery('');
    }
  }, [problem, isOpen]);

  const createServiceRequestMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/cases', data);
      return response.data;
    },
    onSuccess: async (data) => {
      // Extract the case ID from the response (handle different response structures)
      const caseId = data?.case?.id || data?.id;

      if (!caseId) {
        console.error('Failed to get case ID from response:', data);
        setErrorMessage('Service request created but failed to get ID');
        onClose();
        return;
      }

      // Create bidirectional related records linking the service request to the problem
      try {
        // Create relationship from service request to problem
        await api.post(`/cases/${caseId}/related-records`, {
          relationships: [{
            targetId: problem.id,
            relationshipType: 'RELATED_TO'
          }]
        });

        // Create reverse relationship from problem to service request
        await api.post(`/problems/${problem.id}/related-records`, {
          relationships: [{
            targetId: caseId,
            relationshipType: 'RELATED_TO'
          }]
        });
      } catch (error) {
        console.error('Failed to create related record:', error);
      }

      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['service-requests'] });
      queryClient.invalidateQueries({ queryKey: ['related-records', problem.id] });
      queryClient.invalidateQueries({ queryKey: ['related-records', caseId] });
      
      // Call onSuccess callback with service request number if provided
      if (onSuccess) {
        const serviceRequestNumber = data?.case?.number || data?.number || caseId;
        onSuccess(serviceRequestNumber);
      }
      
      onClose();
      // Navigate to the new service request
      navigate(`/service-requests/${caseId}`);
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || 'Failed to create service request';
      setErrorMessage(errorMsg);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const errors: Record<string, string> = {};

    if (!formData.impact) {
      errors.impact = 'Impact is required';
    }
    if (!formData.urgency) {
      errors.urgency = 'Urgency is required';
    }
    if (!formData.shortDescription.trim()) {
      errors.shortDescription = 'Short Description is required';
    }
    if (!formData.customerId) {
      errors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      errors.engagementId = 'Engagement is required';
    }
    if (!formData.contactId) {
      errors.contactId = 'Contact is required';
    }
    if (isOtherResource) {
      if (!formData.otherResourceNames || !formData.otherResourceNames.trim()) {
        errors.resourceId = 'Resource Names is required when Others is selected';
      }
    } else {
      if (!formData.resourceId) {
        errors.resourceId = 'Resource is required';
      }
    }
    if (!formData.assignmentGroupId) {
      errors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setErrorMessage('Please fill in all required fields');
      return;
    }

    setValidationErrors({});
    setErrorMessage(null);

    const payload: any = {
      caseType: 'service_request',
      ticketType: 'SERVICE_REQUEST',
      shortDescription: formData.shortDescription,
      channel: 'web',
      category: 'request',
      priority: formData.priority,
      impact: formData.impact,
      urgency: formData.urgency,
      assignedToId: formData.assignedTo,
      assignmentGroupId: formData.assignmentGroupId || undefined,
      customerId: formData.customerId,
      engagementId: formData.engagementId,
      contactId: formData.contactId,
      servicePlanId: formData.servicePlanId || undefined,
      // Include source problem info for activity tracking
      createdFromProblemId: problem.id,
      createdFromProblemNumber: problem.number
    };

    // Add resource information based on selection type
    if (isOtherResource && formData.otherResourceNames?.trim()) {
      payload.otherResourceNames = formData.otherResourceNames.trim();
    } else if (!isOtherResource && formData.resourceId) {
      payload.resourceId = formData.resourceId;
      payload.resourceIdValue = formData.resourceIdValue || undefined;
    }

    createServiceRequestMutation.mutate(payload);
  };

  const customers = customersData?.customers || [];
  const engagements = engagementsData?.engagements || [];
  const contacts = contactsData?.users || [];
  const servicePlans = servicePlansData?.plans || [];
  const groups = groupsData || [];
  const resources = Array.isArray(resourcesData) ? resourcesData : [];
  const groupUsers = usersData || [];

  // Impact and Urgency options
  const impactOptions = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  const urgencyOptions = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  // Helper function to get impact/urgency label
  const getImpactUrgencyLabel = (value: string): string => {
    const option = [...impactOptions, ...urgencyOptions].find(opt => opt.value === value);
    return option ? option.label : '';
  };

  // Filtered data for comboboxes
  const filteredCustomers = customerQuery === ''
    ? customers
    : customers.filter((c: any) =>
        c.accountName?.toLowerCase().includes(customerQuery.toLowerCase())
      );

  const filteredEngagements = engagementQuery === ''
    ? engagements
    : engagements.filter((e: any) =>
        e.name?.toLowerCase().includes(engagementQuery.toLowerCase())
      );

  const filteredContacts = contacts;

  const filteredResources = resourceQuery === ''
    ? resources
    : resources.filter((r: any) =>
        r.name?.toLowerCase().includes(resourceQuery.toLowerCase())
      );

  const filteredAssignmentGroups = assignmentGroupQuery === ''
    ? groups
    : groups.filter((g: any) =>
        g.name?.toLowerCase().includes(assignmentGroupQuery.toLowerCase())
      );

  const filteredGroupMembers = React.useMemo(() => {
    const members = groupUsers || [];
    if (assignedToQuery === '') {
      return members;
    }
    return members.filter((u: any) =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase())
    );
  }, [groupUsers, assignedToQuery]);

  const filteredImpacts = impactQuery === ''
    ? impactOptions
    : impactOptions.filter((opt) =>
        opt.label.toLowerCase().includes(impactQuery.toLowerCase())
      );

  const filteredUrgencies = urgencyQuery === ''
    ? urgencyOptions
    : urgencyOptions.filter((opt) =>
        opt.label.toLowerCase().includes(urgencyQuery.toLowerCase())
      );

  const filteredServicePlans = servicePlanQuery === ''
    ? servicePlans
    : servicePlans.filter((plan: any) =>
        plan.name?.toLowerCase().includes(servicePlanQuery.toLowerCase())
      );

  // Helper function to get priority label
  const getPriorityLabel = (priority: string): string => {
    const labels: Record<string, string> = {
      'critical_1': '1 - Critical',
      'high_2': '2 - High',
      'moderate_3': '3 - Moderate',
      'low_4': '4 - Low',
      'planning_5': '5 - Planning'
    };
    return labels[priority] || '4 - Low';
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Create Service Request from Problem {problem?.number}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                  {/* Error Message */}
                  {errorMessage && (
                    <div className="mb-6 rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">{errorMessage}</div>
                    </div>
                  )}

                  {/* Row 1: Impact and Urgency */}
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Impact <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={formData.impact}
                            onChange={(value) => {
                              setFormData({ ...formData, impact: value || '' });
                              if (validationErrors.impact) {
                                setValidationErrors(prev => ({ ...prev, impact: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 border rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                                  validationErrors.impact ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                                }`}
                                displayValue={(value: string) => getImpactUrgencyLabel(value)}
                                onChange={(event) => setImpactQuery(event.target.value)}
                                placeholder="Select impact..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setImpactQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredImpacts.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No impacts found.
                                    </div>
                                  ) : (
                                    filteredImpacts.map((impact) => (
                                      <Combobox.Option
                                        key={impact.value}
                                        value={impact.value}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {impact.label}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.impact && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.impact}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Urgency <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={formData.urgency}
                            onChange={(value) => {
                              setFormData({ ...formData, urgency: value || '' });
                              if (validationErrors.urgency) {
                                setValidationErrors(prev => ({ ...prev, urgency: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 border rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                                  validationErrors.urgency ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                                }`}
                                displayValue={(value: string) => getImpactUrgencyLabel(value)}
                                onChange={(event) => setUrgencyQuery(event.target.value)}
                                placeholder="Select urgency..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setUrgencyQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredUrgencies.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No urgencies found.
                                    </div>
                                  ) : (
                                    filteredUrgencies.map((urgency) => (
                                      <Combobox.Option
                                        key={urgency.value}
                                        value={urgency.value}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {urgency.label}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.urgency && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.urgency}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Priority (calculated) and State */}
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Priority
                          </label>
                        </div>
                        <div className="col-span-9">
                          <input
                            type="text"
                            value={formData.priority ? getPriorityLabel(formData.priority) : ''}
                            disabled
                            readOnly
                            className="w-full px-3 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-[38px] disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            State
                          </label>
                        </div>
                        <div className="col-span-9">
                          <input
                            type="text"
                            value="New"
                            disabled
                            readOnly
                            className="w-full px-3 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-[38px] disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Customer and Engagement */}
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Customer <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={formData.customerId}
                            onChange={(value) => {
                              setFormData(prev => ({
                                ...prev,
                                customerId: value || '',
                                engagementId: '',
                                contactId: '',
                                servicePlanId: '',
                                resourceId: '',
                                resourceIdValue: '',
                                otherResourceNames: ''
                              }));
                              setSelectedResource(null);
                              setIsOtherResource(false);
                              if (validationErrors.customerId) {
                                setValidationErrors(prev => ({ ...prev, customerId: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 border rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                                  validationErrors.customerId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                                }`}
                                displayValue={(customerId: string) => {
                                  const customer = customers.find((c: any) => c.id === customerId);
                                  return customer ? customer.accountName : '';
                                }}
                                onChange={(event) => setCustomerQuery(event.target.value)}
                                placeholder="Search customers..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setCustomerQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredCustomers.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No customers found.
                                    </div>
                                  ) : (
                                    filteredCustomers.map((customer: any) => (
                                      <Combobox.Option
                                        key={customer.id}
                                        value={customer.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {customer.accountName}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.customerId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.customerId}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Engagement <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={formData.engagementId}
                            onChange={(value) => {
                              setFormData(prev => ({
                                ...prev,
                                engagementId: value || '',
                                contactId: '',
                                servicePlanId: '',
                                resourceId: '',
                                resourceIdValue: '',
                                otherResourceNames: ''
                              }));
                              setSelectedResource(null);
                              setIsOtherResource(false);
                              if (validationErrors.engagementId) {
                                setValidationErrors(prev => ({ ...prev, engagementId: '' }));
                              }
                            }}
                            disabled={!formData.customerId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 border rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                  validationErrors.engagementId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                                }`}
                                displayValue={(engagementId: string) => {
                                  const engagement = engagements.find((e: any) => e.id === engagementId);
                                  return engagement ? engagement.name : '';
                                }}
                                onChange={(event) => setEngagementQuery(event.target.value)}
                                placeholder={!formData.customerId ? 'Select Customer first' : 'Search engagements...'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setEngagementQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredEngagements.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No engagements found.
                                    </div>
                                  ) : (
                                    filteredEngagements.map((engagement: any) => (
                                      <Combobox.Option
                                        key={engagement.id}
                                        value={engagement.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {engagement.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.engagementId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.engagementId}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 4: Contact and Service Plan */}
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contact <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={formData.contactId}
                            onChange={(value) => {
                              setFormData({ ...formData, contactId: value || '' });
                              if (validationErrors.contactId) {
                                setValidationErrors(prev => ({ ...prev, contactId: '' }));
                              }
                            }}
                            disabled={!formData.customerId || !formData.engagementId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 border rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                  validationErrors.contactId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                                }`}
                                displayValue={(userId: string) => {
                                  const user = contacts.find((u: any) => u.id === userId);
                                  return user ? `${user.firstName} ${user.lastName}` : '';
                                }}
                                onChange={(event) => setContactQuery(event.target.value)}
                                placeholder={!formData.customerId ? 'Select Customer first' : 'Search contacts...'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setContactQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredContacts.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No contacts found.
                                    </div>
                                  ) : (
                                    filteredContacts.map((user: any) => (
                                      <Combobox.Option
                                        key={user.id}
                                        value={user.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {user.firstName} {user.lastName}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.contactId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.contactId}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Service Plan
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={formData.servicePlanId}
                            onChange={(value) => setFormData({ ...formData, servicePlanId: value || '' })}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className="w-full px-3 border border-gray-300 rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400"
                                displayValue={(planId: string) => {
                                  const plan = servicePlans.find((p: any) => p.id === planId);
                                  return plan ? plan.name : '';
                                }}
                                onChange={(event) => setServicePlanQuery(event.target.value)}
                                placeholder="Search service plans..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setServicePlanQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredServicePlans.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No service plans found.
                                    </div>
                                  ) : (
                                    filteredServicePlans.map((plan: any) => (
                                      <Combobox.Option
                                        key={plan.id}
                                        value={plan.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {plan.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 5: Resource and Resource ID */}
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Resource <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={isOtherResource ? '__OTHER__' : formData.resourceId}
                            onChange={(value) => {
                              if (value === '__OTHER__') {
                                setIsOtherResource(true);
                                setSelectedResource(null);
                                setFormData(prev => ({ ...prev, resourceId: '', resourceIdValue: '' }));
                              } else {
                                setIsOtherResource(false);
                                const resource = resources.find((r: any) => r.id === value);
                                if (resource) {
                                  setSelectedResource({
                                    id: resource.id,
                                    name: resource.name || '',
                                    resourceId: resource.resourceId || ''
                                  });
                                  setFormData(prev => ({ ...prev, resourceId: value || '', resourceIdValue: resource.resourceId || '', otherResourceNames: '' }));
                                } else {
                                  setFormData(prev => ({ ...prev, resourceId: value || '', otherResourceNames: '' }));
                                }
                              }
                              if (validationErrors.resourceId) {
                                setValidationErrors(prev => ({ ...prev, resourceId: '' }));
                              }
                            }}
                            disabled={!formData.engagementId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 border rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                  validationErrors.resourceId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                                }`}
                                displayValue={(value: string) => {
                                  if (isOtherResource || value === '__OTHER__') {
                                    return 'Others (specify custom resources)';
                                  }
                                  if (value) {
                                    const resource = resources.find((r: any) => r.id === value);
                                    if (resource) return resource.name || '';
                                    // Fallback to selectedResource state
                                    if (selectedResource && selectedResource.id === value) {
                                      return selectedResource.name;
                                    }
                                    // Fallback to problem's original resource data
                                    if (problem?.Resource && problem.Resource.id === value) {
                                      return problem.Resource.name || '';
                                    }
                                  }
                                  return '';
                                }}
                                onChange={(event) => setResourceQuery(event.target.value)}
                                placeholder={!formData.engagementId ? 'Select Engagement first' : 'Search resources...'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setResourceQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredResources.length === 0 && resourceQuery === '' ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No resources found.
                                    </div>
                                  ) : (
                                    <>
                                      {filteredResources.map((resource: any) => (
                                        <Combobox.Option
                                          key={resource.id}
                                          value={resource.id}
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                              active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                            }`
                                          }
                                        >
                                          {({ active, selected }) => (
                                            <>
                                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                {resource.name}
                                              </span>
                                              {selected && (
                                                <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </Combobox.Option>
                                      ))}
                                      {resourceQuery === '' && (
                                        <Combobox.Option
                                          value="__OTHER__"
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${
                                              active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                            }`
                                          }
                                        >
                                          {({ active, selected }) => (
                                            <>
                                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                Others (specify custom resources)
                                              </span>
                                              {selected && (
                                                <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </Combobox.Option>
                                      )}
                                    </>
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.resourceId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.resourceId}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {isOtherResource ? 'Resource Names' : 'Resource ID'}
                          </label>
                        </div>
                        <div className="col-span-9">
                          {isOtherResource ? (
                            <input
                              type="text"
                              value={formData.otherResourceNames}
                              onChange={(e) => setFormData(prev => ({ ...prev, otherResourceNames: e.target.value }))}
                              className="w-full px-3 border border-gray-300 rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400"
                              placeholder="Enter comma-separated resource names"
                            />
                          ) : (
                            <input
                              type="text"
                              value={formData.resourceIdValue}
                              readOnly
                              disabled
                              className="w-full px-3 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-[38px] disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder={formData.resourceId ? '' : 'Select a resource to see ID'}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 6: Assignment Group and Assigned To */}
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Assignment Group <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={formData.assignmentGroupId}
                            onChange={(value) => {
                              setFormData(prev => ({
                                ...prev,
                                assignmentGroupId: value || '',
                                assignedTo: ''
                              }));
                              if (validationErrors.assignmentGroupId) {
                                setValidationErrors(prev => ({ ...prev, assignmentGroupId: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 border rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                                  validationErrors.assignmentGroupId ? 'border-red-300' : 'border-gray-300'
                                }`}
                                displayValue={(groupId: string) => {
                                  const group = groups.find((g: any) => g.id === groupId);
                                  return group ? group.name : '';
                                }}
                                onChange={(event) => setAssignmentGroupQuery(event.target.value)}
                                placeholder="Search assignment groups..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setAssignmentGroupQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredAssignmentGroups.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No groups found.
                                    </div>
                                  ) : (
                                    filteredAssignmentGroups.map((group: any) => (
                                      <Combobox.Option
                                        key={group.id}
                                        value={group.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {group.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.assignmentGroupId && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignmentGroupId}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Assigned To
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={formData.assignedTo}
                            onChange={(value) => {
                              setFormData(prev => ({ ...prev, assignedTo: value || '' }));
                            }}
                            disabled={!formData.assignmentGroupId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 border rounded text-sm h-[38px] focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                  validationErrors.assignedTo ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                                }`}
                                displayValue={(userId: string) => {
                                  const user = groupUsers.find((u: any) => u.id === userId);
                                  return user ? `${user.firstName} ${user.lastName}` : '';
                                }}
                                onChange={(event) => setAssignedToQuery(event.target.value)}
                                placeholder={!formData.assignmentGroupId ? 'Select Assignment Group first' : 'Search assignees...'}
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setAssignedToQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {filteredGroupMembers.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      No members found.
                                    </div>
                                  ) : (
                                    filteredGroupMembers.map((user: any) => (
                                      <Combobox.Option
                                        key={user.id}
                                        value={user.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                            active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                      >
                                        {({ active, selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {user.firstName} {user.lastName}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {validationErrors.assignedTo && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.assignedTo}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 7: Short Description (full width with rich text editor) */}
                  <div className="grid grid-cols-12 gap-6 mb-6">
                    <div className="col-span-12">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Short Description <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-10">
                          <RichTextEditor
                            value={formData.shortDescription}
                            onChange={(value) => {
                              setFormData(prev => ({ ...prev, shortDescription: value }));
                              if (validationErrors.shortDescription) {
                                setValidationErrors(prev => ({ ...prev, shortDescription: '' }));
                              }
                            }}
                            placeholder="Brief summary of the service request"
                            minHeight="120px"
                            className={validationErrors.shortDescription ? 'border-red-300' : ''}
                          />
                          {validationErrors.shortDescription && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.shortDescription}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createServiceRequestMutation.isPending}
                      className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {createServiceRequestMutation.isPending ? 'Creating...' : 'Create Service Request'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateServiceRequestFromProblemModal;
